import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

export default function Nav() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  const links = [
    { to: "/analyze", label: "Analyze a Home" },
    { to: "/dashboard", label: "My Homes", auth: true },
    { to: "/compare", label: "Compare", auth: true },
    { to: "/coe", label: "VA COE Guide" },
  ];

  const isActive = (to) => loc.pathname === to;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-brand-border bg-white/85 backdrop-blur-md">
      <div className="container-hs flex items-center justify-between py-3">
        <Link to="/" data-testid="nav-brand" aria-label="Home Cost Path home" className="flex items-center gap-2">
          {/* Text wordmark used pending delivery of Home Cost Path logo artwork.
              Two-tone matches the ink/accent brand colors. */}
          <span className="font-display text-xl sm:text-2xl font-semibold text-brand-ink tracking-tight">
            Home<span className="text-brand-accent">CostPath</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map(l => (!l.auth || user) && (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive(l.to) ? "bg-brand-soft text-brand-ink" : "text-brand-muted hover:text-brand-ink hover:bg-brand-soft/60"
              }`}>{l.label}</Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="px-4 py-2 rounded-full text-sm font-medium text-brand-muted hover:text-brand-ink hover:bg-brand-soft/60">Admin</Link>
          )}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {user.entitlement?.plan_type === "premium" ? (
                <span data-testid="nav-plan-badge" className="hidden lg:inline text-[10px] uppercase tracking-wider font-semibold text-brand-accent bg-brand-soft rounded-full px-2 py-0.5">Pass · {user.entitlement.days_remaining}d</span>
              ) : (
                <Link data-testid="nav-upgrade" to="/upgrade" className="hidden lg:inline text-[10px] uppercase tracking-wider font-semibold text-white bg-brand-accent rounded-full px-2 py-0.5 hover:opacity-90">Unlock 90 days</Link>
              )}
              <span className="text-sm text-brand-muted" data-testid="nav-user-email">{user.email}</span>
              <button data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }} className="btn-secondary py-2 px-4 text-sm">Log out</button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="text-sm font-medium text-brand-ink hover:text-brand-accent">Log in</Link>
              <Link data-testid="nav-signup" to="/register" className="btn-primary py-2 px-4 text-sm">Sign up</Link>
            </>
          )}
        </div>
        <button data-testid="nav-menu" className="md:hidden p-2" onClick={() => setOpen(o => !o)}>{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden border-t border-brand-border bg-white">
          <div className="container-hs py-3 flex flex-col gap-2">
            {links.map(l => (!l.auth || user) && (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2 text-brand-ink">{l.label}</Link>
            ))}
            {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-brand-ink">Admin</Link>}
            {user ? (
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-left py-2 text-brand-ink">Log out</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-brand-ink">Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="py-2 text-brand-accent font-medium">Sign up</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
